import { GenerationMethod } from '@heidi/schema/ml-scribe';
import { type TemplateResponse } from '@heidi/templates/contracts';

/**
 * Resolves the label for the template an artifact is using, from its generation
 * method, template and AI command.
 *
 * Not for the V2 note tab or a *note* card title — a note names the object and
 * leaves the template to `V2TemplateRow` (SCR-5734). Sub-document card titles
 * still resolve through here.
 *
 * @param generationMethod - The method used to generate the note
 * @param currentTemplate - The current template being used (if any)
 * @param aiCommandText - Custom AI command text (if any)
 * @param autoModeLabel - Label for auto mode; omit to fall through to `fallbackLabel`
 * @param fallbackLabel - Optional fallback label when no other title is available
 * @returns The display title
 */
export const getNoteTabTitle = ({
  generationMethod,
  currentTemplate,
  aiCommandText,
  autoModeLabel,
  fallbackLabel,
}: {
  generationMethod?: GenerationMethod;
  currentTemplate?: TemplateResponse | null;
  aiCommandText?: string | null;
  autoModeLabel?: string;
  fallbackLabel?: string | null;
}): string | null => {
  // In auto mode, the template hasn't been chosen yet — never show a stale template name.
  // Return autoModeLabel for the button, or fallbackLabel ("Note") for the tab.
  if (generationMethod === GenerationMethod.AUTO_MODE) {
    return autoModeLabel || fallbackLabel || null;
  }

  // When generating from an AI command, the note is no longer tied to any
  // previously-selected template, but `template_id` lingers in the optimistic
  // cache until the end-of-stream refetch clears it. Prefer the AI command text
  // so the title updates immediately instead of showing the stale template name
  // until streaming finishes.
  if (
    generationMethod === GenerationMethod.PRESET_AI_COMMAND ||
    generationMethod === GenerationMethod.AI_COMMAND_TEXT
  ) {
    return aiCommandText || currentTemplate?.name || fallbackLabel || null;
  }

  // Otherwise, return in priority order: template name, AI command text, or fallback
  return currentTemplate?.name || aiCommandText || fallbackLabel || null;
};
